import React from "react";
import { find, isEmpty, uniq, flatMap, includes } from "lodash";

import { useUser } from "../../contexts/user";
import useUserAccessesQuery from "../useUserAccessesQuery";
import useCurriculumQuery from "../useCurriculumQuery";

function isCurriculumAccessibleViaPiano(curriculum, userAccesses = []) {
  if (!curriculum) {
    return undefined;
  }

  return !!userAccesses.find(
    (access) => access.resource.rid === curriculum.pianoRid,
  );
}

function isCurriculumAccessibleViaClassroom(curriculumSlug, user) {
  const selectedClassroomCurriculums = find(
    user?.classrooms,
    (classroom) =>
      classroom.id === user?.selectedClassroomId &&
      !isEmpty(classroom.curriculumAccesses) &&
      !isEmpty(classroom.curriculums),
  );

  if (!selectedClassroomCurriculums || !curriculumSlug) {
    return undefined;
  }

  const accessibleCurriculumsIds = uniq(
    flatMap(selectedClassroomCurriculums.curriculumAccesses, "curriculums"),
  );

  return includes(accessibleCurriculumsIds, curriculumSlug);
}

export default function useHasAccessToCurriculumQuery(slug, options = {}) {
  const { user } = useUser();
  const {
    curriculum,
    isCurriculumLoading,
    curriculumError,
  } = useCurriculumQuery(slug, options);
  const {
    userAccesses,
    isUserAccessesLoading,
    userAccessesError,
  } = useUserAccessesQuery({
    enabled: user?.features?.piano === true,
    ...options,
  });

  const hasAccessToCurriculum = React.useMemo(() => {
    if (!user?.isLoggedIn) {
      return undefined;
    }

    if (!user?.selectedClassroomId) {
      return undefined;
    }

    if (user?.features?.piano && user?.features?.pianoCurriculumAccess) {
      return isCurriculumAccessibleViaPiano(curriculum, userAccesses);
    }

    return isCurriculumAccessibleViaClassroom(slug, user);
  }, [curriculum, slug, user, userAccesses]);

  return {
    hasAccessToCurriculum,
    isHasAccessToCurriculumLoading:
      isCurriculumLoading || isUserAccessesLoading,
    hasAccessToCurriculumError: curriculumError || userAccessesError,
  };
}
